import { format } from 'date-fns';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CopyIcon } from '../../../../../assets/images/icons';
import {
  ATTRIBUTION_SOURCE_OPTIONS,
  AttributionLinkSource,
  formatAttributionSourceOptionLabel,
  isAttributionLinkSource,
} from '../../../../../common/constants/attribution-link-source';
import { Button } from '../../../../../common/ui/button';
import { FormItem } from '../../../../../common/ui/formItem';
import { Loader } from '../../../../../common/ui/loader';
import { Modal } from '../../../../../common/ui/modal';
import { Notification } from '../../../../../common/ui/notification';
import { Select } from '../../../../../common/ui/select';
import { SingleDatePicker } from '../../../../../common/ui/single-date-picker';
import { TextField } from '../../../../../common/ui/text-field';
import { utilCopyToClipboard } from '../../../../../common/utils/utilCopyToClipboard';
import { AggPageFormContext } from '../../../../../context/page-form-context';
import { PagesContext } from '../../../../../context/pages-context';
import { useAppSelector } from '../../../../../hooks';
import {
  generateTemporaryLinksSlice,
} from '../../../../../store/reducers/temporary-links/generate-temporary-links-slice';
import styles from './generate-link-modal.module.scss';

interface GenerateLinkModalProps {
  asin: string;
  initialSource?: string | null;
  onClose: () => void;
  productId: string;
}

const resolveInitialSource = (initialSource?: string | null): AttributionLinkSource =>
  isAttributionLinkSource(initialSource)
    ? initialSource
    : AttributionLinkSource.PartnerBoost;

export const GenerateLinkModal = ({
  asin,
  initialSource,
  onClose,
  productId,
}: GenerateLinkModalProps) => {
  const { publisher } = useContext(PagesContext);
  const {
    error,
    isLoading,
    links
  } = useAppSelector(({ generateTemporaryLinks }) => generateTemporaryLinks);
  const dispatch = useDispatch();
  const { updateProduct } = useContext(AggPageFormContext);

  const [expiredAt, setExpiredAt] = useState<Date>(null);
  const [source, setSource] = useState<AttributionLinkSource>(resolveInitialSource(initialSource));
  const [generatedLink] = [...links?.created || [], ...links?.updated || []];

  const generateLink = () => {
    const region = publisher?.country || 'us';
    dispatch(generateTemporaryLinksSlice.actions.generate({
      meta: {
        onSuccess: (_id, data) => updateProduct(productId, data),
      },
      payload: {
        asins: [asin],
        ...(expiredAt ? { expired_at: format(expiredAt, 'yyyy-MM-dd') } : {}),
        publisher_id: publisher.id || 0,
        region,
        source,
      },
    }));
  };

  useEffect(() => () => {
    dispatch(generateTemporaryLinksSlice.actions.resetState());
  }, []);

  return <Modal className={styles.root} open>
    <Modal.Title>Generate link</Modal.Title>
    <Modal.Body className={styles.body}>
      <FormItem label="ASIN">
        <div className="d-flex column-gap-10 w-100">
          <TextField disabled fullWidth value={asin} />
          <Button
            iconStart={<CopyIcon />}
            onClick={() => utilCopyToClipboard(asin)}
            outlined
            variant="secondary"
          />
        </div>
      </FormItem>
      <FormItem label="Provider">
        <Select
          className={styles.provider_select}
          formatOptionLabel={formatAttributionSourceOptionLabel}
          isDisabled={isLoading}
          isSearchable={false}
          menuPlacement="auto"
          onChange={(option) => setSource(option.value)}
          options={ATTRIBUTION_SOURCE_OPTIONS}
          value={ATTRIBUTION_SOURCE_OPTIONS.find((o) => o.value === source)}
        />
      </FormItem>
      <FormItem label="Expiration date (optinal)">
        <SingleDatePicker
          disabled={isLoading}
          onChange={(selectedDay) => setExpiredAt(selectedDay)}
          value={expiredAt}
        />
      </FormItem>

      {isLoading && (
        <div className={styles.loaderWrapper}>
          <Loader size="small" />
        </div>
      )}
      {!isLoading && generatedLink?.link
          && <div className={styles.linkRow}>
            <span
              className={styles.link}
              onClick={() => window.open(generatedLink.link)}
              title={generatedLink.link}
            >
              {generatedLink.link}
            </span>
            <Button
              iconStart={<CopyIcon />}
              onClick={() => utilCopyToClipboard(generatedLink?.link)}
              outlined
              variant="secondary"
            />
          </div>}
      {!isLoading && error && <Notification fullWidth message={error?.message} type="error" />}
    </Modal.Body>
    <Modal.Actions>
      <Button
        disabled={isLoading}
        onClick={onClose}
        outlined
        variant="secondary"
      >
        Cancel
      </Button>
      {!generatedLink?.link && <Button disabled={isLoading} onClick={generateLink}>
        Generate
      </Button>}
    </Modal.Actions>
  </Modal>;
};
